// ── Grenade Projectile Manager ── Client-side prediction and rendering
// of in-flight grenades. Uses small color-coded spheres with gravity + bounce
// for smooth motion between server snapshot updates.

import * as THREE from 'three';
import { GRENADE_GRAVITY, GRENADE_BOUNCE, type GrenadeId } from '../core/config';
import { EFFECT_LAYER } from '../renderer/effect-loop';
import { audioManager } from '../audio/audio-manager';
import { floorYAtCached, beginFloorLookupFrame } from './floor-lookup';

/** Color map for grenade projectile meshes. */
const GRENADE_COLORS: Record<string, number> = {
  he_grenade: 0xcc3333,     // red
  smoke_grenade: 0x999999,  // gray
  flashbang: 0xeeeeee,      // white
  molotov: 0xff8833,        // orange
  incendiary: 0xff8833,     // orange
};

/** Stale timeout in ms — if server hasn't reported a projectile for this long, remove it. */
const STALE_PROJECTILE_MS = 2000;

interface ActiveProjectile {
  id: number;
  type: string;
  mesh: THREE.Mesh;
  velocity: THREE.Vector3;
  hasBounced: boolean;
  bounceCount: number;
  trail: THREE.Sprite[];
  lastTrailTime: number;
  /** Timestamp (performance.now()) when server last included this projectile in snapshot data. */
  lastServerSeen: number;
}

/** Trail color per grenade type. */
function getTrailColor(type: string): number {
  switch (type) {
    case 'he_grenade': return 0xff4444;
    case 'smoke_grenade': return 0x999999;
    case 'flashbang': return 0xeeeeee;
    case 'molotov': case 'incendiary': return 0xff8833;
    default: return 0xcccccc;
  }
}

export class GrenadeProjectileManager {
  private projectiles = new Map<number, ActiveProjectile>();
  private scene: THREE.Scene | null = null;

  /** Spawn a new grenade projectile in the scene. */
  spawnProjectile(
    id: number,
    type: string,
    pos: THREE.Vector3,
    vel: THREE.Vector3,
    scene: THREE.Scene,
  ): void {
    this.scene = scene;
    // Don't duplicate
    if (this.projectiles.has(id)) return;

    const color = GRENADE_COLORS[type] ?? 0xcccccc;
    const geo = new THREE.SphereGeometry(0.15, 8, 6);
    const mat = new THREE.MeshBasicMaterial({ color }); // BasicMaterial = always visible, no lighting needed
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.copy(pos);
    mesh.userData.isEffect = true;
    mesh.layers.set(EFFECT_LAYER);
    scene.add(mesh);

    this.projectiles.set(id, {
      id,
      type,
      mesh,
      velocity: vel.clone(),
      hasBounced: false,
      bounceCount: 0,
      trail: [],
      lastTrailTime: 0,
      lastServerSeen: performance.now(),
    });
  }

  /**
   * Update all active projectiles.
   * Uses client-side physics prediction (gravity + bounce) for smooth motion,
   * then snaps toward server positions when server data is available.
   *
   * @param dt Frame delta in seconds
   * @param serverGrenades Array of server grenade states: { id, type, position, velocity }
   */
  update(dt: number, serverGrenades?: Array<{ id: number; type: string; position: { x: number; y: number; z: number }; velocity?: { x: number; y: number; z: number } }>): void {
    // Reset the per-frame floor-Y cache once before iterating projectiles.
    // The cache lets clustered grenades dedupe BVH raycasts at the same XZ.
    beginFloorLookupFrame();

    // Client-side physics prediction for all projectiles
    for (const proj of this.projectiles.values()) {
      // Gravity
      proj.velocity.y += GRENADE_GRAVITY * dt;

      // Move
      proj.mesh.position.addScaledVector(proj.velocity, dt);

      // Look up the floor surface immediately below the projectile. We pass
      // `fromY = currentY + 0.5` so the ray sees only surfaces at or below
      // the projectile (avoids catching a catwalk above the projectile that
      // sits in a drainage trench below). On a JSON map / pre-BVH frame this
      // returns the legacy fallback of 0 — keeping the original behavior.
      const groundY = floorYAtCached(proj.mesh.position.x, proj.mesh.position.z, {
        fromY: proj.mesh.position.y + 0.5,
      });
      const bounceY = groundY + 0.06;

      // Bounce off ground (was hardcoded y ~ 0; G-H-001 — use floor lookup
      // so grenades bounce on catwalks, drainage floors, multi-story decks).
      if (proj.mesh.position.y <= bounceY) {
        proj.mesh.position.y = bounceY;
        proj.velocity.y = -proj.velocity.y * GRENADE_BOUNCE;
        proj.velocity.x *= GRENADE_BOUNCE;
        proj.velocity.z *= GRENADE_BOUNCE;
        proj.hasBounced = true;

        if (proj.bounceCount < 3) {
          audioManager.playAt('grenade_bounce', proj.mesh.position, 0.15);
          proj.bounceCount++;
        }

        // Stop if nearly still
        if (Math.abs(proj.velocity.y) < 0.5) {
          proj.velocity.y = 0;
        }
      }

      // Ground friction (matches server GrenadeGroundFriction = 5.0)
      if (proj.mesh.position.y <= bounceY + 0.01) {
        const hSpeed = Math.sqrt(proj.velocity.x * proj.velocity.x + proj.velocity.z * proj.velocity.z);
        if (hSpeed > 0) {
          const newHSpeed = Math.max(0, hSpeed - 5.0 * dt);
          const scale = newHSpeed / hSpeed;
          proj.velocity.x *= scale;
          proj.velocity.z *= scale;
        }
      }

      // Spin for visual flair
      proj.mesh.rotation.x += dt * 8;
      proj.mesh.rotation.z += dt * 4;

      // Spawn trail sprites every 50ms
      if (this.scene) {
        const now = performance.now();
        if (now - proj.lastTrailTime > 50) {
          const trailMat = new THREE.SpriteMaterial({
            color: getTrailColor(proj.type),
            transparent: true,
            opacity: 0.4,
          });
          const trail = new THREE.Sprite(trailMat);
          trail.position.copy(proj.mesh.position);
          trail.scale.set(0.2, 0.2, 0.2);
          trail.layers.set(EFFECT_LAYER);
          trail.userData.isEffect = true;
          this.scene.add(trail);
          proj.trail.push(trail);
          proj.lastTrailTime = now;
        }

        // Fade and clean up old trail sprites (1.2 opacity/sec, framerate-independent)
        for (let i = proj.trail.length - 1; i >= 0; i--) {
          const t = proj.trail[i];
          const tMat = t.material as THREE.SpriteMaterial;
          tMat.opacity -= 1.2 * dt;
          if (tMat.opacity <= 0) {
            this.scene.remove(t);
            tMat.dispose();
            proj.trail.splice(i, 1);
          }
        }
      }
    }

    // Reconcile with server state when available
    if (serverGrenades) {
      const serverIds = new Set<number>();

      for (const sg of serverGrenades) {
        serverIds.add(sg.id);
        const proj = this.projectiles.get(sg.id);
        if (proj) {
          // Lerp toward server position for smooth reconciliation
          const target = new THREE.Vector3(sg.position.x, sg.position.y, sg.position.z);
          proj.mesh.position.lerp(target, 0.3);

          // Update velocity from server if provided
          if (sg.velocity) {
            proj.velocity.set(sg.velocity.x, sg.velocity.y, sg.velocity.z);
          }

          // Reset stale timer — server is still tracking this grenade
          proj.lastServerSeen = performance.now();
        }
        // If we don't have this projectile yet, the grenade_throw event should spawn it
      }

      // Remove stale projectiles that the server no longer reports.
      // The detonate event normally triggers removal, but if the event is missed
      // (packet loss, timing), this timeout ensures cleanup to prevent memory leaks.
      const now = performance.now();
      for (const [id, proj] of this.projectiles) {
        if (!serverIds.has(id)) {
          if (now - proj.lastServerSeen > STALE_PROJECTILE_MS) {
            // Stale — clean up mesh, geometry, material, and trails
            if (this.scene) {
              for (const t of proj.trail) {
                this.scene.remove(t);
                (t.material as THREE.SpriteMaterial).dispose();
              }
              proj.trail.length = 0;
              this.scene.remove(proj.mesh);
            }
            proj.mesh.geometry.dispose();
            (proj.mesh.material as THREE.Material).dispose();
            this.projectiles.delete(id);
          }
        }
      }
    }
  }

  /** Remove a specific projectile (e.g., on detonation). */
  removeProjectile(id: number, scene: THREE.Scene): void {
    const proj = this.projectiles.get(id);
    if (!proj) return;

    // Clean up trail sprites (iterate backwards; remove each safely)
    for (let i = proj.trail.length - 1; i >= 0; i--) {
      try {
        scene.remove(proj.trail[i]);
        (proj.trail[i].material as THREE.SpriteMaterial).dispose();
      } catch (_e) { /* sprite already removed or disposed */ }
    }
    proj.trail.length = 0;

    scene.remove(proj.mesh);
    proj.mesh.geometry.dispose();
    (proj.mesh.material as THREE.Material).dispose();
    this.projectiles.delete(id);
  }

  /** Get position of a projectile by ID (for spawning effects at detonation point). */
  getPosition(id: number): THREE.Vector3 | null {
    const proj = this.projectiles.get(id);
    return proj ? proj.mesh.position.clone() : null;
  }

  /** Clean up all projectiles. */
  dispose(scene: THREE.Scene): void {
    for (const proj of this.projectiles.values()) {
      // Clean up trail sprites (iterate backwards; remove each safely)
      for (let i = proj.trail.length - 1; i >= 0; i--) {
        try {
          scene.remove(proj.trail[i]);
          (proj.trail[i].material as THREE.SpriteMaterial).dispose();
        } catch (_e) { /* sprite already removed or disposed */ }
      }
      proj.trail.length = 0;

      scene.remove(proj.mesh);
      proj.mesh.geometry.dispose();
      (proj.mesh.material as THREE.Material).dispose();
    }
    this.projectiles.clear();
  }
}
